import { Button } from '@/components/ui/button';
import { env } from '@/env/client';
import {
  RPConfig,
  RPLayout,
  RPPages,
  RPProvider,
  RPTheme,
  ZoomLevel,
  useZoomContext,
} from '@pdf-viewer/react';
import { Maximize, Minus, Plus } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface PdfViewerProps {
  url: string;
}

const ZoomOverlay = () => {
  const { currentZoom, setZoomLevel } = useZoomContext();

  const zoomPercent = Math.round(currentZoom * 100);

  return (
    <div className="border-border/70 bg-background/80 absolute right-2 bottom-2 z-20 flex items-center gap-1 rounded-lg border px-1 py-1 opacity-70 shadow-sm backdrop-blur-md transition-opacity hover:opacity-100">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setZoomLevel((prev) => Math.max(prev - 25, 25))}
        aria-label="Zoom out">
        <Minus className="size-4" />
      </Button>
      <span className="text-muted-foreground min-w-10 text-center text-xs tabular-nums select-none">
        {zoomPercent}%
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setZoomLevel((prev) => Math.min(prev + 25, 500))}
        aria-label="Zoom in">
        <Plus className="size-4" />
      </Button>
      <div className="bg-border mx-0.5 h-4 w-px" />
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setZoomLevel(ZoomLevel.PAGE_WIDTH)}
        aria-label="Fit to width">
        <Maximize className="size-3.5" />
      </Button>
    </div>
  );
};

/** Dev-only: hides the react-pdf license watermark when the prod key doesn't cover localhost. */
const useHideWatermark = (
  containerRef: React.RefObject<HTMLDivElement | null>,
) => {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const container = containerRef.current;
    if (!container) return;

    const hide = (el: Element) => {
      if (
        el instanceof HTMLElement &&
        el.style.getPropertyPriority('visibility') === 'important'
      ) {
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      }
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLElement) hide(node);
        }
      }
    });

    // Hide any already-present watermark
    container.querySelectorAll("[data-rp='pages'] > *").forEach(hide);
    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [containerRef]);
};

export const PdfViewer = ({ url }: PdfViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useHideWatermark(containerRef);

  return (
    <div
      ref={containerRef}
      className="pdf-viewer-no-toolbar h-full overflow-hidden rounded-lg border">
      <RPConfig licenseKey={env.VITE_PUBLIC_REACT_PDF_LICENSE_KEY}>
        <RPTheme>
          <RPProvider src={url}>
            <div className="relative h-full">
              <RPLayout toolbar={false} style={{ height: '100%' }}>
                <RPPages />
              </RPLayout>
              <ZoomOverlay />
            </div>
          </RPProvider>
        </RPTheme>
      </RPConfig>
    </div>
  );
};
